import axios from 'axios';
import AxiosInstance from './AxiosInterceptor';
import { IChangePass, IResetPass } from 'store/ducks/globalTypes';

export interface CreateSchemaResponse {
  message: string;
}

const ApiService = {
  login: (data: { email: string; password: string }) =>
    AxiosInstance({
      url: '/api/login',
      method: 'post',
      data,
      skipAuthHandler: true,
    }),

  superAdminLogin: (data: { email: string; password: string }) =>
    AxiosInstance({
      url: '/api/superuser-login',
      method: 'post',
      data,
    }),

  verifyLogin: (token: string) =>
    AxiosInstance({
      url: '/api/verify-login',
      method: 'get',
      params: { token },
    }),

  verifySuperAdminLogin: (token: string) =>
    AxiosInstance({
      url: '/api/verify-superuser-login',
      method: 'get',
      params: { token },
    }),

  logout: () =>
    AxiosInstance({
      url: '/api/logout',
      method: 'post',
    }),

  forgotPassword: (email: string) =>
    AxiosInstance({
      url: '/api/forgot-password',
      method: 'post',
      data: { email },
    }),

  resetPassword: (formData: IResetPass) =>
    AxiosInstance({
      url: '/api/reset-password',
      method: 'post',
      data: formData,
    }),

  signup: (data: any) =>
    AxiosInstance.post('/api/signup', data, {
      skipAuthHandler: true,
    }),

  changePass: (
    formData: IChangePass,
    token: string | qs.ParsedQs | string[] | qs.ParsedQs[],
  ) =>
    AxiosInstance({
      url: '/api/change-password',
      method: 'post',
      params: { token },
      data: formData,
    }),

  getFilters: (financial_entity_lei_id?: number) =>
    AxiosInstance({
      url: '/api/entities-by-lei',
      method: 'get',
      params: { financial_entity_lei_id },
    }),

  getFiltersByEntity: () =>
    AxiosInstance({
      url: '/api/entities',
      method: 'get',
    }),

  postEntity: (entityData: any, token: string) =>
    AxiosInstance({
      url: '/api/insert-entity',
      method: 'post',
      params: { token },
      data: entityData,
    }),

  getRT0101: (data: { entityId: string[]; date?: string }) => {
    const paramsString = data.entityId.map((id) => `entity_id=${id}`).join('&');
    const url = `/api/RT-01-01?${paramsString}${data.date ? `&date=${data.date}` : ''}`;
    return AxiosInstance({
      url,
      method: 'get',
    });
  },

  getRT0102: (data: { entityId: string[]; date?: string }) => {
    const paramsString = data.entityId.map((id) => `entity_id=${id}`).join('&');
    const url = `/api/RT-01-02?${paramsString}${data.date ? `&date=${data.date}` : ''}`;
    return AxiosInstance({
      url,
      method: 'get',
    });
  },

  getBranches: (data: { entityId: string[]; date?: string }) => {
    const paramsString = data.entityId.map((id) => `entity_id=${id}`).join('&');
    const url = `/api/entity-branches?${paramsString}${data.date ? `&date=${data.date}` : ''}`;
    return AxiosInstance({
      url,
      method: 'get',
    });
  },

  getRT0201: (data: { entityId: string[]; date?: string }) => {
    const paramsString = data.entityId.map((id) => `entity_id=${id}`).join('&');
    const url = `/api/RT-02-01?${paramsString}${data.date ? `&date=${data.date}` : ''}`;
    return AxiosInstance({
      url,
      method: 'get',
    });
  },

  getRT0202: (data: { entityId: string[]; date?: string }) => {
    const paramsString = data.entityId.map((id) => `entity_id=${id}`).join('&');
    const url = `/api/RT-02-02?${paramsString}${data.date ? `&date=${data.date}` : ''}`;
    return AxiosInstance({
      url,
      method: 'get',
    });
  },

  getEntityContractualArrangements: (data: {
    entityId: string[];
    date?: string;
  }) => {
    const paramsString = data.entityId.map((id) => `entity_id=${id}`).join('&');
    const url = `/api/entity-contractual-arrangements?${paramsString}${data.date ? `&date=${data.date}` : ''}`;
    return AxiosInstance({
      url,
      method: 'get',
    });
  },

  getContractualArrangementsLei: (data: {
    entityId: string[];
    date?: string;
  }) => {
    const paramsString = data.entityId.map((id) => `entity_id=${id}`).join('&');
    const url = `/api/contractual-arrangements-lei?${paramsString}${data.date ? `&date=${data.date}` : ''}`;
    return AxiosInstance({
      url,
      method: 'get',
    });
  },

  getRT0302: (data: { entityId: string[]; date?: string }) => {
    const paramsString = data.entityId.map((id) => `entity_id=${id}`).join('&');
    const url = `/api/RT-03-02?${paramsString}${data.date ? `&date=${data.date}` : ''}`;
    return AxiosInstance({
      url,
      method: 'get',
    });
  },

  getContractualArrangementsICT: (data: {
    entityId: string[];
    date?: string;
  }) => {
    const paramsString = data.entityId.map((id) => `entity_id=${id}`).join('&');
    const url = `/api/contractual-arrangements-ict?${paramsString}${data.date ? `&date=${data.date}` : ''}`;
    return AxiosInstance({
      url,
      method: 'get',
    });
  },

  getContractualArrangementsICTUse: (data: {
    entityId: string[];
    date?: string;
  }) => {
    const paramsString = data.entityId.map((id) => `entity_id=${id}`).join('&');
    const url = `/api/contractual-arrangements-ict-use?${paramsString}${data.date ? `&date=${data.date}` : ''}`;
    return AxiosInstance({
      url,
      method: 'get',
    });
  },

  getRT0501: (data: { entityId: string[]; date?: string }) => {
    const paramsString = data.entityId.map((id) => `entity_id=${id}`).join('&');
    const url = `/api/RT-05-01?${paramsString}${data.date ? `&date=${data.date}` : ''}`;
    return AxiosInstance({
      url,
      method: 'get',
    });
  },

  getRT0502: (data: { entityId: string[]; date?: string }) => {
    const paramsString = data.entityId.map((id) => `entity_id=${id}`).join('&');
    const url = `/api/RT-05-02?${paramsString}${data.date ? `&date=${data.date}` : ''}`;
    return AxiosInstance({
      url,
      method: 'get',
    });
  },

  getRT0601: (data: { entityId: string[]; date?: string }) => {
    const paramsString = data.entityId.map((id) => `entity_id=${id}`).join('&');
    const url = `/api/RT-06-01?${paramsString}${data.date ? `&date=${data.date}` : ''}`;
    return AxiosInstance({
      url,
      method: 'get',
    });
  },

  getRT0701: (data: { entityId: string[]; date?: string }) => {
    const paramsString = data.entityId.map((id) => `entity_id=${id}`).join('&');
    const url = `/api/RT-07-01?${paramsString}${data.date ? `&date=${data.date}` : ''}`;
    return AxiosInstance({
      url,
      method: 'get',
    });
  },

  getB9901: (data: { entityId: string[]; date?: string }) => {
    const paramsString = data.entityId.map((id) => `entity_id=${id}`).join('&');
    const url = `/api/report-99?${paramsString}${data.date ? `&date=${data.date}` : ''}`;
    return AxiosInstance({
      url,
      method: 'get',
    });
  },

  getReportDetails: (table_name: any, entity_id: number) =>
    AxiosInstance({
      url: '/api/get-report-99-details',
      method: 'post',
      data: { table_name, entity_id },
    }),

  getAR0101: (data: any) => {
    return AxiosInstance({
      url: '/api/AR-01-01',
      method: 'get',
      params: {
        entity_id: data.entity_name,
        date: data.date,
      },
    });
  },

  getAR0102: (data: any) => {
    return AxiosInstance({
      url: '/api/AR-01-02',
      method: 'get',
      params: {
        entity_id: data.entity_name,
        date: data.date,
      },
    });
  },

  getAR0103: (data: any) => {
    return AxiosInstance({
      url: '/api/AR-01-03',
      method: 'get',
      params: {
        entity_id: data.entity_name,
        date: data.date,
      },
    });
  },

  getReportData: (data: any) => {
    return AxiosInstance({
      url: '/api/report-data',
      method: 'get',
      params: {
        entity_id: data.entity_name,
        date: data.date,
      },
    });
  },

  getReportFunctions: (data: any) => {
    return AxiosInstance({
      url: '/api/report-functions',
      method: 'get',
      params: {
        entity_id: data.entity_name,
        date: data.date,
      },
    });
  },

  getReportTPPs: (data: any) => {
    return AxiosInstance({
      url: '/api/report-third_party_service_provider',
      method: 'get',
      params: {
        entity_id: data.entity_name,
        date: data.date,
      },
    });
  },

  getCertificatesByICTServices: (data: any) => {
    return AxiosInstance({
      url: '/api/filter-certificates-by-ict-service',
      method: 'get',
      params: {
        entity_id: data.entity_name,
        ict_service_id: data.ict_service_id,
        date: data.date,
      },
    });
  },

  getCertificatesByFunction: (data: any) => {
    return AxiosInstance({
      url: '/api/filter-certificates-by-function',
      method: 'get',
      params: {
        entity_id: data.entity_name,
        function_id: data.function_id,
        date: data.date,
      },
    });
  },

  getCertificatesByICTSystemsFunction: (data: any) => {
    return AxiosInstance({
      url: '/api/filter-certificates-by-ict-system-function',
      method: 'get',
      params: {
        entity_id: data.entity_name,
        ict_system_id: data.ict_system_id,
        date: data.date,
      },
    });
  },

  getCertificatesByICTSystemsICTServices: (data: any) => {
    return AxiosInstance({
      url: '/api/filter-certificates-by-ict-system-service',
      method: 'get',
      params: {
        entity_id: data.entity_name,
        ict_system_id: data.ict_system_id,
        date: data.date,
      },
    });
  },

  getEntitiesByFinancialLei: (data: any) => {
    return AxiosInstance({
      url: '/api/filter-entities-by-financial-lei',
      method: 'get',
      params: {
        financial_entity_lei: data.financial_entity_lei,
        date: data.date,
      },
    });
  },

  getReportICTServices: (data: any) => {
    return AxiosInstance({
      url: '/api/report-ict-service',
      method: 'get',
      params: {
        entity_id: data.entity_name,
        date: data.date,
      },
    });
  },

  getReportICTSystems: (data: any) => {
    return AxiosInstance({
      url: '/api/report-ict-systems',
      method: 'get',
      params: {
        entity_id: data.entity_name,
        date: data.date,
      },
    });
  },

  postEntityType: (entityData: any) =>
    AxiosInstance({
      url: '/api/insert-entity-type',
      method: 'post',
      data: entityData,
    }),

  postHierarchyGroup: (hierarchyGroupData: any) =>
    AxiosInstance({
      url: '/api/insert-hierarchy-group',
      method: 'post',
      data: hierarchyGroupData,
    }),

  getAllEntities: () => {
    return AxiosInstance({
      url: '/api/get-all-entities',
      method: 'get',
    });
  },

  postEntityTypeByEntityName: (entityData: any) =>
    AxiosInstance({
      url: '/api/get-entity-type',
      method: 'post',
      data: entityData,
    }),

  postHierarchyGroupByEntityName: (entityData: any) =>
    AxiosInstance({
      url: '/api/get-entity-hierarchy',
      method: 'post',
      data: entityData,
    }),

  postNatureOfEntityByEntityName: (entityData: any) =>
    AxiosInstance({
      url: '/api/get-entity-nature',
      method: 'post',
      data: entityData,
    }),

  postCompetentAuthorityByEntityName: (entityData: any) =>
    AxiosInstance({
      url: '/api/get-entity-competent-authority',
      method: 'post',
      data: entityData,
    }),

  postCountryByEntityName: (entityData: any) =>
    AxiosInstance({
      url: '/api/get-entity-country',
      method: 'post',
      data: entityData,
    }),

  postValueOfTotalAssetsByEntityName: (entityData: any) =>
    AxiosInstance({
      url: '/api/get-entity-value-of-total-assets',
      method: 'post',
      data: entityData,
    }),

  postActivitiesByEntityName: (entityData: any) =>
    AxiosInstance({
      url: '/api/get-entity-activities-by-entity-type',
      method: 'post',
      data: entityData,
    }),

  postEntitiesByEntityName: (entityData: any) =>
    AxiosInstance({
      url: '/api/get-entity-by-id',
      method: 'post',
      data: entityData,
    }),

  postTPPbyEntityName: (tpp: any) =>
    AxiosInstance({
      url: '/api/get-entity-third-party-service-provider',
      method: 'post',
      data: tpp,
    }),

  postNatureOfEntity: (natureOfEntity: any) =>
    AxiosInstance({
      url: '/api/insert-nature-of-entity',
      method: 'post',
      data: natureOfEntity,
    }),

  postCompetentAuthority: (competentAuthority: any) =>
    AxiosInstance({
      url: '/api/insert-competent-authority',
      method: 'post',
      data: competentAuthority,
    }),
  postCountry: (country: any) =>
    AxiosInstance({
      url: '/api/insert-country',
      method: 'post',
      data: country,
    }),

  postValueOfTotalAssets: (valueOfTotalAssets: any) =>
    AxiosInstance({
      url: '/api/insert-value-of-total-assets',
      method: 'post',
      data: valueOfTotalAssets,
    }),

  postActivitiesByTypeEntity: (activitiesByTypeEntity: any) =>
    AxiosInstance({
      url: '/api/insert-activities-by-type-entity',
      method: 'post',
      data: activitiesByTypeEntity,
    }),

  getNonSubstReason: () => {
    return AxiosInstance({
      url: '/api/list-non-subst-reasons',
      method: 'get',
    });
  },

  fetchNonSubstReason: (tpp_name: any, tpp_id: string) =>
    AxiosInstance({
      url: '/api/get-non-subst-reason-tpp',
      method: 'post',
      data: { tpp_name, tpp_id },
    }),

  fetchPersonType: (tpp_name: any, tpp_id: string) =>
    AxiosInstance({
      url: '/api/get-person-type-tpp',
      method: 'post',
      data: { tpp_name, tpp_id },
    }),

  fetchCurrencyAndCosts: (tpp_name: any, tpp_id: string) =>
    AxiosInstance({
      url: '/api/get-costs-and-currency-tpp',
      method: 'post',
      data: { tpp_name, tpp_id },
    }),

  fetchCertificates: (tpp_name: string, tpp_id: string) =>
    AxiosInstance({
      url: '/api/filter-certificates-by-tpp',
      method: 'post',
      data: { tpp_name, tpp_id },
    }),

  postTPP: (postTPP: any) =>
    AxiosInstance({
      url: '/api/insert-third-party-service-provider',
      method: 'post',
      data: postTPP,
    }),
  postNonSubstReason: (nonSubstReason: any) =>
    AxiosInstance({
      url: '/api/insert-non-subst-reason',
      method: 'post',
      data: nonSubstReason,
    }),

  postPersonTypes: (personTypes: any) =>
    AxiosInstance({
      url: '/api/insert-person-type',
      method: 'post',
      data: personTypes,
    }),

  postCurrencyAndCosts: (currencyAndCosts: any) =>
    AxiosInstance({
      url: '/api/insert-currency-costs',
      method: 'post',
      data: currencyAndCosts,
    }),
  postCertificates: (certificates: any) =>
    AxiosInstance({
      url: '/api/insert-certificates',
      method: 'post',
      data: certificates,
    }),

  postBranchByEntityName: (entityData: any) =>
    AxiosInstance({
      url: '/api/get-branch-country-assets-by-entity',
      method: 'post',
      data: entityData,
    }),

  postBranches: (branches: any) =>
    AxiosInstance({
      url: '/api/insert-branch-country-assets',
      method: 'post',
      data: branches,
    }),

  postFunctionByEntityName: (entityData: any) =>
    AxiosInstance({
      url: '/api/filter-function-details',
      method: 'post',
      data: entityData,
    }),

  postFunctions: (functions: any) =>
    AxiosInstance({
      url: '/api/insert-function-details',
      method: 'post',
      data: functions,
    }),

  postFunctionsbyEntityName: (entityData: any) =>
    AxiosInstance({
      url: '/api/get-entity-functions',
      method: 'post',
      data: entityData,
    }),

  postFunctionsByEntity: (entityData: any) =>
    AxiosInstance({
      url: '/api/list-functions-by-entity',
      method: 'post',
      data: entityData,
    }),

  postContractualArrangementsbyEntityName: (entityData: any) =>
    AxiosInstance({
      url: '/api/get-contractual-arrangements-entity',
      method: 'post',
      data: entityData,
    }),

  fetchICTServices: (
    selectedContractForICT: any,
    selectedEntityForICT: any,
    selectedFunctionForICT: any,
    selectedTPPForICT: any,
  ) => {
    return AxiosInstance({
      url: '/api/filter-ict-services',
      method: 'post',
      data: {
        entity_id: selectedEntityForICT ? selectedEntityForICT.entityId : null,
        entity_name: selectedEntityForICT ? selectedEntityForICT.entity : null,
        contractual_arrangements_id: selectedContractForICT
          ? selectedContractForICT.value
          : null,
        // tpp_id: selectedTPPForICT ? selectedTPPForICT.value : null,
        // tpp_name: selectedTPPForICT ? selectedTPPForICT.label : null,
        function_id: selectedFunctionForICT
          ? selectedFunctionForICT.value
          : null,
        function_name: selectedFunctionForICT
          ? selectedFunctionForICT.label
          : null,
      },
    });
  },

  fetchDataWithICT: (
    selectedEntityForICT: any,
    selectedICTForContractualInformation: any,
  ) => {
    return AxiosInstance({
      url: '/api/filter-ict-service-ict-system-details',
      method: 'post',
      data: {
        entity_id: selectedEntityForICT ? selectedEntityForICT.entityId : null,
        ict_service_id: selectedICTForContractualInformation
          ? selectedICTForContractualInformation.value
          : null,
      },
    });
  },

  fetchDataWithFunctions: (
    selectedEntityForICT: any,
    selectedFunctionForICT: any,
  ) => {
    return AxiosInstance({
      url: '/api/filter-function-ict-system-details',
      method: 'post',
      data: {
        entity_id: selectedEntityForICT ? selectedEntityForICT.entityId : null,
        function_id: selectedFunctionForICT
          ? selectedFunctionForICT.value
          : null,
      },
    });
  },

  postICTServices: (ICTServices: any) =>
    AxiosInstance({
      url: '/api/insert-ict-service-details',
      method: 'post',
      data: ICTServices,
    }),

  postBranchesbyEntityName: (entityData: any) =>
    AxiosInstance({
      url: '/api/get-branches-entity',
      method: 'post',
      data: entityData,
    }),

  postICTServicesbyEntityName: (entityData: any) =>
    AxiosInstance({
      url: '/api/get-ict-services-entity',
      method: 'post',
      data: entityData,
    }),

  fetchContractualInformation: (
    selectedBranchForContractualInformation: any,
    selectedICTForContractualInformation: any,
    selectedTPPForContractualInformation: any,
    selectedEntityForContractualInformation: any,
  ) => {
    return AxiosInstance({
      url: '/api/filter-contractual-arrangements',
      method: 'post',
      data: {
        branch_id: selectedBranchForContractualInformation
          ? selectedBranchForContractualInformation.value
          : null,
        branch_name: selectedBranchForContractualInformation
          ? selectedBranchForContractualInformation.label
          : null,
        entity_id: selectedEntityForContractualInformation
          ? selectedEntityForContractualInformation.entityId
          : null,
        entity_name: selectedEntityForContractualInformation
          ? selectedEntityForContractualInformation.entity
          : null,
        tpp_id: selectedTPPForContractualInformation
          ? selectedTPPForContractualInformation.value
          : null,
        tpp_name: selectedTPPForContractualInformation
          ? selectedTPPForContractualInformation.label
          : null,
        ict_service_id: selectedICTForContractualInformation
          ? selectedICTForContractualInformation.value
          : null,
      },
    });
  },

  postContractualInformation: (contractualInformation: any) =>
    AxiosInstance({
      url: '/api/insert-contractual-details',
      method: 'post',
      data: contractualInformation,
    }),

  fetchUserActivityStatus: (username: string) =>
    AxiosInstance({
      url: '/api/toggle_user_status',
      method: 'post',
      data: { username },
    }),

  getLicensedActivities: () =>
    AxiosInstance({
      url: '/api/get-licensed-activities',
      method: 'get',
    }),

  postLicensedActivities: (entity_id: any, entity_name: any) =>
    AxiosInstance({
      url: '/api/get-entity-selection-licenced-act',
      method: 'post',
      data: { entity_id, entity_name },
    }),

  getICTServiceTypes: () =>
    AxiosInstance({
      url: '/api/get-ict-service-types',
      method: 'get',
    }),

  getICTSystemTypes: () =>
    AxiosInstance({
      url: '/api/list-ict-system-types',
      method: 'get',
    }),

  getICTSystemCategories: () =>
    AxiosInstance({
      url: '/api/list-ict-system-categories',
      method: 'get',
    }),

  getTerminatReasons: () =>
    AxiosInstance({
      url: '/api/get-terminat-reasons',
      method: 'get',
    }),

  getSensitivenessOfData: (entity_id: number) =>
    AxiosInstance({
      url: '/api/get-sensitiveness-of-data',
      method: 'get',
      params: { entity_id },
    }),

  getLevelsOfReliance: () =>
    AxiosInstance({
      url: '/api/get-levels-of-reliance',
      method: 'get',
    }),

  getCriticalityAssessments: () =>
    AxiosInstance({
      url: '/api/get-criticality-assessments',
      method: 'get',
    }),

  getICTServiceSubstitutability: (entity_id: number) =>
    AxiosInstance({
      url: '/api/get-ict-provider-substitutability',
      method: 'get',
      params: { entity_id },
    }),

  getReintegrationPossibilities: (entity_id: number) =>
    AxiosInstance({
      url: '/api/get-reintegration-possibilities',
      method: 'get',
      params: { entity_id },
    }),

  getImpactOfDiscontinuingICTServices: (entity_id: number) =>
    AxiosInstance({
      url: '/api/get-impact-of-discontinuing-ict-services',
      method: 'get',
      params: { entity_id },
    }),

  getImpactOfDiscontinuingFunctions: (entity_id: number) =>
    AxiosInstance({
      url: '/api/get-impact-of-discontinuing-function',
      method: 'get',
      params: { entity_id },
    }),

  getAlternativesExit: () =>
    AxiosInstance({
      url: '/api/get-alternatives-exit',
      method: 'get',
    }),

  getEntityTypes: () =>
    AxiosInstance({
      url: '/api/get-entity-types',
      method: 'get',
    }),

  getHierarchyInGroups: () =>
    AxiosInstance({
      url: '/api/get-hierarchy-groups',
      method: 'get',
    }),

  getNatureID: () =>
    AxiosInstance({
      url: '/api/get-nature-of-entities',
      method: 'get',
    }),

  getCompetentAuthorities: () =>
    AxiosInstance({
      url: '/api/get-competent-authorities',
      method: 'get',
    }),

  getCountries: () =>
    AxiosInstance({
      url: '/api/get-countries',
      method: 'get',
    }),

  getCurrencies: () =>
    AxiosInstance({
      url: '/api/get-currencies',
      method: 'get',
    }),

  getListOfBranches: () =>
    AxiosInstance({
      url: '/api/list-branches',
      method: 'get',
    }),

  getListOfFunctions: () =>
    AxiosInstance({
      url: '/api/list-functions',
      method: 'get',
    }),

  getValueOfTotalAssets: () =>
    AxiosInstance({
      url: '/api/get-value-of-total-assets',
      method: 'get',
    }),

  getContractualArrangementTypes: (entity_id: number) =>
    AxiosInstance({
      url: '/api/get-contractual-arrangement-types',
      method: 'get',
      params: {
        entity_id,
      },
    }),

  getContractTypes: () =>
    AxiosInstance({
      url: '/api/get-contract-types',
      method: 'get',
    }),

  getContractualArrangements: () =>
    AxiosInstance({
      url: '/api/get-contractual-arrangements',
      method: 'get',
    }),

  getAuswahlFunctionDiscont: () =>
    AxiosInstance({
      url: '/api/get-auswahl-function-discont',
      method: 'get',
    }),

  getFunctionLevels: () =>
    AxiosInstance({
      url: '/api/get-function-levels',
      method: 'get',
    }),

  getFunctions: () =>
    AxiosInstance({
      url: '/api/get-functions',
      method: 'get',
    }),

  getICTServices: () =>
    AxiosInstance({
      url: '/api/get-ict-services',
      method: 'get',
    }),

  getDataSensitiveness: () =>
    AxiosInstance({
      url: '/api/get-data-sensitiveness',
      method: 'get',
    }),

  getCosts: () =>
    AxiosInstance({
      url: '/api/get-costs',
      method: 'get',
    }),

  getNonSubstReasons: () =>
    AxiosInstance({
      url: '/api/get-non-subst-reasons',
      method: 'get',
    }),

  getListPersonTypes: () =>
    AxiosInstance({
      url: '/api/list-person-types',
      method: 'get',
    }),

  getICTSystems: () =>
    AxiosInstance({
      url: '/api/get-ict-systems',
      method: 'get',
    }),

  getProviderCodeTypes: () =>
    AxiosInstance({
      url: '/api/get-third-party-service-provider-code-types',
      method: 'get',
    }),

  getFinancialEntities: () => {
    return AxiosInstance({
      url: '/api/get-financial-entities',
      method: 'get',
    });
  },

  getFinancialEntityById: (entityId: number | null) =>
    AxiosInstance({
      url: '/api/get-financial-entity-by-id',
      method: 'post',
      data: { financial_entity_id: entityId },
    }),

  updateRecoveryObjective: (recoveryObjective: any) =>
    AxiosInstance({
      url: '/api/update-function-recovery-objectives',
      method: 'post',
      data: recoveryObjective,
    }),

  postFinancialEntity: (entityData: any) =>
    AxiosInstance({
      url: '/api/insert-financial-entity-lei',
      method: 'post',
      data: entityData,
    }),

  getICTLevels: () =>
    AxiosInstance({
      url: '/api/get-ict-service-levels',
      method: 'get',
    }),

  getSupportLevel: () =>
    AxiosInstance({
      url: '/api/get-level-of-supporting-ict-service',
      method: 'get',
    }),

  getYesNo: () =>
    AxiosInstance({
      url: '/api/get-yes-no-status',
      method: 'get',
    }),

  getDoraRegulatorCriticality: () =>
    AxiosInstance({
      url: '/api/get-dora-regulator-criticality',
      method: 'get',
    }),

  getFunctionsCriticality: () =>
    AxiosInstance({
      url: '/api/get-functions-criticality',
      method: 'get',
    }),

  getICTServicesCriticality: () =>
    AxiosInstance({
      url: '/api/get-ict-services-criticality',
      method: 'get',
    }),

  getLocalRegulatorCriticality: () =>
    AxiosInstance({
      url: '/api/get-local-regulator-criticality',
      method: 'get',
    }),

  getDoraCriticality: () =>
    AxiosInstance({
      url: '/api/get-dora-criticality',
      method: 'get',
    }),

  postICTSystemsByEntity: (entity: any) =>
    AxiosInstance({
      url: '/api/get-ict-systems-entity',
      method: 'post',
      data: entity,
    }),

  postICTSystemsByEntityCategoryFunction: (entity: any) =>
    AxiosInstance({
      url: '/api/get-ict-systems-entity-category-function',
      method: 'post',
      data: entity,
    }),

  postICTSystemsByEntityCategoryICTService: (entity: any) =>
    AxiosInstance({
      url: '/api/get-ict-systems-entity-category-ict-service',
      method: 'post',
      data: entity,
    }),

  getActiveUsers: () =>
    AxiosInstance({
      url: '/api/get-active-users',
      method: 'get',
    }),

  getUserFunctions: (data: any) => {
    return AxiosInstance({
      url: '/api/get-user-functions',
      method: 'get',
      params: {
        user_id: data.user_id,
      },
    });
  },

  assignUserFunctions: (user_id: number, function_ids: any) =>
    AxiosInstance({
      url: '/api/assign-user-functions',
      method: 'post',
      data: { user_id, function_ids },
    }),

  removeUserFunctions: (user_id: number, function_ids: any) =>
    AxiosInstance({
      url: '/api/remove-user-functions',
      method: 'post',
      data: { user_id, function_ids },
    }),

  updateReportDetails: (table_name: any, records: any) =>
    AxiosInstance({
      url: '/api/create-update-report-99',
      method: 'post',
      data: { table_name, records },
    }),

  getUserFunctionRoles: (active: boolean) =>
    AxiosInstance({
      url: '/api/user-function-roles',
      method: 'post',
      data: { active },
    }),

  getActiveInactiveOptions: () =>
    AxiosInstance({
      url: '/api/active-inactive-dropdown',
      method: 'post',
      data: {},
    }),

  fetchGlobalUsers: () =>
    AxiosInstance({
      url: '/api/get-global-users',
      method: 'get',
    }),

  signupSuperUser: (data: any) =>
    AxiosInstance({
      url: '/api/signup-superuser',
      method: 'post',
      data,
    }),

  softDeleteUser: (email: number, schema_id: any) =>
    AxiosInstance({
      url: '/api/superadmin-soft-delete-user',
      method: 'post',
      data: { email, schema_id },
    }),

  postGlobalCompanies: (data: any) =>
    AxiosInstance({
      url: '/api/insert-global-company',
      method: 'post',
      data,
    }),

  fetchGlobalCompanies: () =>
    AxiosInstance({
      url: '/api/get-global-companies',
      method: 'get',
    }),

  fetchGlobalCompanyID: (global_company_id: number, schema_id: string) =>
    AxiosInstance({
      url: '/api/get-global-company-by-id',
      method: 'post',
      data: { global_company_id, schema_id },
    }),

  createClientSchema: (schemaId: string, file: File) => {
    const fd = new FormData();
    fd.append('schema_id', schemaId);
    fd.append('sql_file', file, file.name);
    return AxiosInstance.post('/api/create-client-schema', fd);
  },

  uploadExcelEntityTypeActivity: (schemaId: string, file: File) => {
    const fd = new FormData();
    fd.append('schema_id', schemaId);
    fd.append('file', file, file.name);
    return AxiosInstance.post('/api/upload-excel-entity-type-activity', fd);
  },

  uploadExcelAccessControl: (schemaId: string, file: File) => {
    const fd = new FormData();
    fd.append('schema_id', schemaId);
    fd.append('file', file, file.name);
    return AxiosInstance.post('/api/upload-access-control-excel', fd);
  },

  uploadParentData: (schemaId: string, file: File) => {
    const fd = new FormData();
    fd.append('schema_id', schemaId);
    fd.append('excel_file', file, file.name);
    return AxiosInstance.post('/api/upload-parent-data', fd);
  },

  validateLEI: (leis: string[]) =>
    AxiosInstance({
      url: '/api/validate-leis',
      method: 'post',
      data: { leis },
    }),

  getRiskExposure: () => {
    return AxiosInstance({
      url: '/api/get-risk-exposure',
      method: 'get',
    });
  },

  getDependencyType: () => {
    return AxiosInstance({
      url: '/api/get-dependency-type',
      method: 'get',
    });
  },

  postRiskExposure: (riskExposure: any) =>
    AxiosInstance({
      url: '/api/insert-risk-exposure',
      method: 'post',
      data: riskExposure,
    }),

  postDependencyType: (riskExposure: any) =>
    AxiosInstance({
      url: '/api/insert-dependency-type',
      method: 'post',
      data: riskExposure,
    }),

  getExposureCategory: () => {
    return AxiosInstance({
      url: '/api/get-exposure-category',
      method: 'get',
    });
  },

  postExposureCategory: (exposureCategory: any) =>
    AxiosInstance({
      url: '/api/insert-exposure-category',
      method: 'post',
      data: exposureCategory,
    }),

  getICTSystemCriticality: () => {
    return AxiosInstance({
      url: '/api/get-ict-system-criticality',
      method: 'get',
    });
  },

  postICTSystemCriticality: (exposureCategory: any) =>
    AxiosInstance({
      url: '/api/insert-ict-system-criticality',
      method: 'post',
      data: exposureCategory,
    }),

  getEntityRole: () => {
    return AxiosInstance({
      url: '/api/get-entity-role',
      method: 'get',
    });
  },

  postEntityRole: (entity_role: any) =>
    AxiosInstance({
      url: '/api/insert-entity-role',
      method: 'post',
      data: entity_role,
    }),
  getRoleType: () => {
    return AxiosInstance({
      url: '/api/get-role-type',
      method: 'get',
    });
  },

  postRoleType: (roleType: any) =>
    AxiosInstance({
      url: '/api/insert-role-type',
      method: 'post',
      data: roleType,
    }),

  getICTRisk: (entity_id: number) => {
    return AxiosInstance({
      url: '/api/get-ict-risk-entity',
      method: 'get',
      params: {
        entity_id,
      },
    });
  },

  postICTRisk: (ictRisk: any) =>
    AxiosInstance({
      url: '/api/insert-ict-risk',
      method: 'post',
      data: ictRisk,
    }),

  getRiskSource: () => {
    return AxiosInstance({
      url: '/api/get-risk-source',
      method: 'get',
    });
  },

  postRiskSource: (risk_source: any) =>
    AxiosInstance({
      url: '/api/insert-risk-source',
      method: 'post',
      data: risk_source,
    }),

  getRiskLevel: () => {
    return AxiosInstance({
      url: '/api/get-risk-level',
      method: 'get',
    });
  },

  postRiskLevel: (risk_lvl: any) =>
    AxiosInstance({
      url: '/api/insert-risk-level',
      method: 'post',
      data: risk_lvl,
    }),

  getResponsibilities: () => {
    return AxiosInstance({
      url: '/api/get-responsibilities',
      method: 'get',
    });
  },

  postResponsibilities: (responsibility: any) =>
    AxiosInstance({
      url: '/api/insert-responsibilities',
      method: 'post',
      data: responsibility,
    }),

  getICTSystemsByICTService: (entity_id: number) =>
    AxiosInstance({
      url: '/api/filter-ict-system-details-ict-service',
      method: 'post',
      data: { entity_id },
    }),

  getICTSystemsByFunction: (entity_id: number) =>
    AxiosInstance({
      url: '/api/filter-ict-system-details-function',
      method: 'post',
      data: { entity_id },
    }),

  getICTRiskServiceDetails: (entity_id: any) =>
    AxiosInstance({
      url: '/api/get-ict-risk-service-details',
      method: 'get',
      params: { entity_id },
    }),

  getICTRiskFunctionDetails: (entity_id: any) =>
    AxiosInstance({
      url: '/api/get-ict-risk-functions-details',
      method: 'get',
      params: { entity_id },
    }),

  getRolesAndResponsibilities: (entity_id: string | number, date: string) =>
    AxiosInstance({
      url: '/api/get-role-with-responsibilities-entity',
      method: 'get',
      params: { entity_id, date },
    }),

  getICTRiskSource: (entity_id: string | number, date: string) =>
    AxiosInstance({
      url: '/api/get-ict-risk-source-details',
      method: 'get',
      params: { entity_id, date },
    }),

  getICTExposure: (entity_id: string | number, date: string) =>
    AxiosInstance({
      url: '/api/get-ict-exposure-details',
      method: 'get',
      params: { entity_id, date },
    }),

  getICTRiskExposureFunctionDetails: (
    entity_id: string | number,
    date: string,
  ) =>
    AxiosInstance({
      url: '/api/get-ict-risk-exposure-functions-details',
      method: 'get',
      params: { entity_id, date },
    }),

  getICTRiskExposureService: (entity_id: string | number, date: string) =>
    AxiosInstance({
      url: '/api/get-ict-risk-exposure-service-details',
      method: 'get',
      params: { entity_id, date },
    }),

  getICTSystemsDetailsByService: (entity_id: number, date: string) =>
    AxiosInstance({
      url: '/api/get-ict-system-details-ict-service',
      method: 'get',
      params: { entity_id, date },
    }),

  getICTSystemsDetailsByFunction: (entity_id: number, date: string) =>
    AxiosInstance({
      url: '/api/get-ict-system-details-function',
      method: 'get',
      params: { entity_id, date },
    }),

  filterEntityFunctionChain: (entity_id: number, date: string) =>
    AxiosInstance({
      url: '/api/filter-entity-function-chain',
      method: 'get',
      params: { entity_id, date },
    }),

  filterEntityTPPChain: (entity_id: number, date: string) =>
    AxiosInstance({
      url: '/api/filter-entity-tpp-chain',
      method: 'get',
      params: { entity_id, date },
    }),

  filterEntityFunctionReverse: (entity_id: number, date: string) =>
    AxiosInstance({
      url: '/api/filter-entity-function-reverse',
      method: 'get',
      params: { entity_id, date },
    }),

  filterEntitybyFinancialEntities: (financial_entity_lei_id: number) =>
    AxiosInstance({
      url: '/api/list-entities-by-financial-entity-lei',
      method: 'post',
      data: { financial_entity_lei_id },
    }),

  getListOfThirdPartyServiceProviders: () =>
    AxiosInstance({
      url: '/api/list-third-party-service-providers',
      method: 'get',
    }),

  getListOfThirdPartyServiceProvidersByEntity: (entity_id: number) =>
    AxiosInstance({
      url: '/api/list-third-party-service-providers-by-entity',
      method: 'post',
      data: { entity_id },
    }),

  getListOfContractualArrangements: () =>
    AxiosInstance({
      url: '/api/list-contractual-arrangements',
      method: 'get',
    }),

  getListOfContractualArrangementsByEntity: (entity_id: number) =>
    AxiosInstance({
      url: '/api/list-contractual-arrangements-by-tpsp',
      method: 'post',
      data: { third_party_service_provider_id: entity_id },
    }),

  getListOfICTServices: () =>
    AxiosInstance({
      url: '/api/list-ict-services',
      method: 'get',
    }),

  getListOfICTServicesByContractualArrangement: (
    contractual_arrangement_id: number,
  ) =>
    AxiosInstance({
      url: '/api/list-ict-services-by-contractual-arrangement',
      method: 'post',
      data: { contractual_arrangement_id },
    }),

  getListOfICTSystems: () =>
    AxiosInstance({
      url: '/api/list-ict-systems',
      method: 'get',
    }),

  getListOfICTSystemsByEntity: (entity_id: number) =>
    AxiosInstance({
      url: '/api/list-ict-systems-by-entity',
      method: 'post',
      data: { entity_id },
    }),

  getSchemaTables: () =>
    AxiosInstance({
      url: '/api/get-schema-tables',
      method: 'get',
    }),

  checkDeleteDependency: (id: number, table_name: string) =>
    AxiosInstance({
      url: '/api/check-delete-dependency',
      method: 'get',
      params: { id, table_name },
    }),

  softDeleterecord: (id: number, table_name: string, confirm: boolean) =>
    AxiosInstance({
      url: '/api/soft-delete-record',
      method: 'post',
      params: { id, table_name, confirm },
    }),

  inactivateTPP: (dataList: number[]) =>
    AxiosInstance({
      url: '/api/inactivate-third-party-service-provider',
      method: 'post',
      data: { data_list: dataList },
    }),
};

export default ApiService;
